import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSignupCaptcha } from "../hooks/useSignupCaptcha";
import { SignupCaptcha } from "../components/SignupCaptcha";

const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-[#FAFAF7]">
    <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
      <h1 className="font-display text-2xl font-bold tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1.5">{subtitle}</p>}
      <div className="mt-6">{children}</div>
    </div>
  </div>
);

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500";
const btnCls = "w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl active:scale-[0.98] transition-all disabled:opacity-60";

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      if (data.must_change_password) { navigate("/change-password"); return; }
      const next = params.get("next");
      if (next) navigate(next);
      else if (data.role === "admin" || data.membership !== "free") navigate("/app");
      else navigate("/pricing");
    } catch (e2) {
      setErr(apiError(e2));
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Welcome back" subtitle="Log in to your AI Shorthand dictionary.">
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="login-email-input" className={inputCls} type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input data-testid="login-password-input" className={inputCls} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        {err && <p data-testid="login-error" className="text-sm text-red-500">{err}</p>}
        <button data-testid="login-submit-btn" className={btnCls} disabled={busy}>{busy ? "Logging in…" : "Log in"}</button>
      </form>
      <div className="flex items-center justify-between mt-5 text-sm">
        <Link to="/forgot-password" data-testid="forgot-password-link" className="text-emerald-700 font-medium hover:underline">Forgot password?</Link>
        <Link to="/register" data-testid="go-register-link" className="text-slate-500 hover:text-slate-800">Create account</Link>
      </div>
    </Shell>
  );
}

export function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser, user } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) { setErr("Passwords do not match"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pwd });
      if (user) setUser({ ...user, must_change_password: false });
      toast.success("Password updated — welcome!");
      navigate("/app");
    } catch (e2) { setErr(apiError(e2)); }
    setBusy(false);
  };

  return (
    <Shell title="Set a new password" subtitle="For security, please replace your temporary password before continuing.">
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="change-current-input" className={inputCls} type="password" placeholder="Temporary (current) password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        <input data-testid="change-new-input" className={inputCls} type="password" placeholder="New password (min 6 characters)" value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={6} />
        <input data-testid="change-confirm-input" className={inputCls} type="password" placeholder="Confirm new password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} required />
        {err && <p data-testid="change-password-error" className="text-sm text-red-500">{err}</p>}
        <button data-testid="change-password-btn" className={btnCls} disabled={busy}>{busy ? "Saving…" : "Save new password"}</button>
      </form>
    </Shell>
  );
}

export function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", voucher_code: "", terms_accepted: false });
  const [voucherState, setVoucherState] = useState(null); // null | "checking" | {percent} | "invalid"
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { captcha, loadCaptcha, setAnswer } = useSignupCaptcha(busy);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refParam = params.get("ref");
  if (refParam) localStorage.setItem("referral_code", refParam);
  const referralCode = refParam || localStorage.getItem("referral_code") || "";
  const isTrial = params.get("plan") === "trial";

  const checkVoucher = async (code) => {
    if (!code.trim()) { setVoucherState(null); return; }
    setVoucherState("checking");
    try {
      const { data } = await api.get("/vouchers/validate", { params: { code } });
      setVoucherState(data);
    } catch { setVoucherState("invalid"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.terms_accepted || captcha.loading || !captcha.id || !captcha.answer) {
      setErr("Complete the human check and accept the terms to continue.");
      return;
    }
    if (Date.now() >= captcha.expiresAt) {
      setErr("Please answer the new human-check question to continue.");
      loadCaptcha();
      return;
    }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/register", {
        ...form,
        referral_code: referralCode,
        captcha_id: captcha.id,
        captcha_answer: captcha.answer,
      });
      localStorage.removeItem("referral_code");
      setUser(data);
      const plan = params.get("plan");
      // "trial" is our card-less 24-hour pass, not a Stripe plan — send them
      // to the /start-trial confirmation page instead of a Stripe checkout URL.
      const nextPath = plan === "trial" ? "/start-trial" : plan ? `/checkout/${plan}` : "/pricing";
      if (data.email_verified) {
        navigate(nextPath);
      } else {
        const failed = data.verification_email_sent === false ? "&emailFailed=1" : "";
        navigate(`/verify-email?next=${encodeURIComponent(nextPath)}${failed}`);
      }
    } catch (e2) {
      setErr(apiError(e2));
      // Any failure (wrong captcha, email in use, etc.) → rotate the sum so
      // bots can't just retry the same answer and so humans never see a stale prompt.
      loadCaptcha();
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Create your account" subtitle="">
      {isTrial && (
        <div data-testid="register-trial-no-card-banner" className="mb-4 rounded-xl border border-emerald-500/40 bg-emerald-50 px-4 py-3 flex items-start gap-2.5">
          <ShieldCheck size={18} className="text-emerald-700 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-bold text-emerald-900">No credit or debit card needed on registration</p>
            <p className="text-xs text-emerald-800 mt-0.5">Your 24-hour free pass starts the moment you verify your email — zero payment details required.</p>
          </div>
        </div>
      )}
      {referralCode && (
        <p data-testid="register-referral-note" className="text-xs text-emerald-700 bg-emerald-50 rounded-xl px-4 py-2.5 mb-4 font-semibold">
          🎁 You were referred by a friend — they'll earn a free ebook when you join a paid plan.
        </p>
      )}
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="register-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="register-email-input" className={inputCls} type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        <input data-testid="register-password-input" className={inputCls} type="password" placeholder="Password (min 6 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
        <div>
          <input data-testid="register-voucher-input" className={inputCls} placeholder="Discount code (optional)" value={form.voucher_code}
            onChange={(e) => { setForm({ ...form, voucher_code: e.target.value }); setVoucherState(null); }}
            onBlur={(e) => checkVoucher(e.target.value)} />
          {voucherState === "checking" && <p className="text-xs text-slate-400 mt-1.5">Checking code…</p>}
          {voucherState === "invalid" && <p data-testid="voucher-invalid-msg" className="text-xs text-red-500 mt-1.5">Invalid or expired discount code</p>}
          {voucherState && voucherState.percent && (
            <p data-testid="voucher-valid-msg" className="text-xs text-emerald-700 font-semibold mt-1.5">✓ {voucherState.percent}% discount will be applied at checkout</p>
          )}
        </div>
        <SignupCaptcha captcha={captcha} refresh={loadCaptcha} setAnswer={setAnswer} busy={busy} />
        {err && <p data-testid="register-error" className="text-sm text-red-500">{err}</p>}
        <label className="flex items-start gap-2.5 text-xs text-slate-500 cursor-pointer" data-testid="register-terms-note">
          <input type="checkbox" className="mt-0.5 accent-emerald-700" checked={form.terms_accepted}
            onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })} data-testid="register-terms-checkbox" />
          <span>I have read and accept the <Link to="/terms" className="underline hover:text-slate-700">Terms of Service</Link> and <Link to="/ai-disclaimer" className="underline hover:text-slate-700">AI Disclaimer &amp; Acceptable Use</Link> policy.</span>
        </label>
        <button data-testid="register-submit-btn" className={btnCls} disabled={busy || !form.terms_accepted || captcha.loading || !captcha.id || !captcha.answer}>{busy ? "Creating…" : "Create account"}</button>
      </form>
      <p className="text-sm text-slate-500 mt-5 text-center">Already a member? <Link to="/login" className="text-emerald-700 font-medium hover:underline">Log in</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) { toast.error(apiError(e2)); }
  };
  return (
    <Shell title="Reset your password" subtitle="Enter your email and we'll send a secure reset link.">
      {sent ? (
        <p data-testid="forgot-sent-message" className="text-sm text-emerald-700 bg-emerald-50 rounded-xl p-4">If an account exists with that email, a reset link has been sent.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <input data-testid="forgot-email-input" className={inputCls} type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <button data-testid="forgot-submit-btn" className={btnCls}>Send reset link</button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password });
      toast.success("Password updated. Please log in.");
      navigate("/login");
    } catch (e2) { setErr(apiError(e2)); }
  };
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="reset-password-input" className={inputCls} type="password" placeholder="New password (min 6 characters)" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
        {err && <p className="text-sm text-red-500">{err}</p>}
        <button data-testid="reset-submit-btn" className={btnCls}>Update password</button>
      </form>
    </Shell>
  );
}

export function VerifyEmail() {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/pricing";
  const emailFailed = params.get("emailFailed") === "1";

  // If already verified, jump straight past this page.
  useEffect(() => {
    if (user && user.email_verified) navigate(nextPath, { replace: true });
  }, [user, nextPath, navigate]);

  // Resend cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/verify-email", { code: code.trim() });
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {}
      toast.success("Email verified");
      navigate(nextPath);
    } catch (e2) {
      setErr(apiError(e2));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true); setErr("");
    try {
      await api.post("/auth/resend-verification");
      toast.success("A fresh code is on its way — check your inbox.");
      setCooldown(60);
    } catch (e2) {
      setErr(apiError(e2));
    } finally { setResending(false); }
  };

  return (
    <Shell title="Verify your email" subtitle={user ? `We sent a 6-digit code to ${user.email}. Enter it below to activate your account.` : "Enter the 6-digit code we sent to your email."}>
      {emailFailed && (
        <div data-testid="verify-email-failed-banner" className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-bold mb-1">We couldn't send your verification email just now.</p>
          <p className="leading-relaxed">
            Our email provider is temporarily unavailable. Click <b>Resend code</b> below to try again, or
            {" "}<Link to="/contact" className="underline font-semibold">contact support</Link> and we'll
            verify your account manually within minutes.
          </p>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="verify-email-form">
        <input
          data-testid="verify-email-code-input"
          className={inputCls + " text-center font-mono text-2xl tracking-[0.5em]"}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          required
        />
        {err && <p data-testid="verify-email-error" className="text-sm text-red-500">{err}</p>}
        <button data-testid="verify-email-submit-btn" className={btnCls} disabled={busy || code.length !== 6}>
          {busy ? "Verifying..." : "Verify & continue"}
        </button>
      </form>
      <div className="mt-5 text-center text-sm text-slate-500">
        Didn't get it?{" "}
        <button
          type="button"
          onClick={resend}
          disabled={cooldown > 0 || resending}
          data-testid="verify-email-resend-btn"
          className="text-emerald-700 font-medium hover:underline disabled:opacity-50 disabled:hover:no-underline"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? "Sending..." : "Resend code"}
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-4 text-center">
        Check your spam folder if it doesn't arrive within a minute.
      </p>
    </Shell>
  );
}

